import { ed25519 } from '@noble/curves/ed25519.js';
import { sha512 } from '@noble/hashes/sha2.js';
import { bytesToBase64Url, toArrayBuffer } from '../utils/encoding';

export interface FallbackEd25519SigningKey {
  type: 'ed25519-fallback';
  privateKey: Uint8Array;
  privateKeyJwk: JsonWebKey;
}

export type SigningKey = CryptoKey | FallbackEd25519SigningKey;

function getSubtleCrypto(): SubtleCrypto | null {
  try {
    return globalThis.crypto?.subtle || null;
  } catch {
    return null;
  }
}

function toBase64Url(bytes: Uint8Array): string {
  return bytesToBase64Url(bytes);
}

export function normalizeEd25519PrivateKey32(privateKey: Uint8Array): Uint8Array {
  if (privateKey.length === 32) return privateKey;
  if (privateKey.length === 64) return privateKey.slice(0, 32);
  throw new Error(`Invalid Ed25519 private key length: ${privateKey.length}`);
}

export function createEd25519PrivateJwk(
  privateKey: Uint8Array,
  publicKey: Uint8Array
): JsonWebKey {
  const privateKey32 = normalizeEd25519PrivateKey32(privateKey);
  return {
    kty: 'OKP',
    crv: 'Ed25519',
    x: toBase64Url(publicKey),
    d: toBase64Url(privateKey32),
    ext: true,
    key_ops: ['sign']
  };
}

function createFallbackSigningKey(
  privateKey: Uint8Array,
  publicKey: Uint8Array
): FallbackEd25519SigningKey {
  return {
    type: 'ed25519-fallback',
    privateKey: normalizeEd25519PrivateKey32(privateKey),
    privateKeyJwk: createEd25519PrivateJwk(privateKey, publicKey)
  };
}

export function isFallbackEd25519SigningKey(signingKey: SigningKey): signingKey is FallbackEd25519SigningKey {
  return typeof signingKey === 'object' && signingKey !== null && 'type' in signingKey && signingKey.type === 'ed25519-fallback';
}

async function signWithNativeEd25519(message: Uint8Array, privateKeyJwk: JsonWebKey): Promise<Uint8Array | null> {
  const subtle = getSubtleCrypto();
  if (!subtle) return null;

  try {
    const key = await subtle.importKey(
      'jwk',
      privateKeyJwk,
      { name: 'Ed25519' },
      false,
      ['sign']
    );
    const signature = await subtle.sign('Ed25519', key, toArrayBuffer(message));
    return new Uint8Array(signature);
  } catch {
    return null;
  }
}

async function verifyWithNativeEd25519(
  message: Uint8Array,
  signature: Uint8Array,
  publicKey: Uint8Array
): Promise<boolean | null> {
  const subtle = getSubtleCrypto();
  if (!subtle) return null;

  try {
    const key = await subtle.importKey(
      'raw',
      toArrayBuffer(publicKey),
      { name: 'Ed25519' },
      false,
      ['verify']
    );
    return subtle.verify(
      'Ed25519',
      key,
      toArrayBuffer(signature),
      toArrayBuffer(message)
    );
  } catch {
    // Browsers/runtimes may not support Ed25519 verify in WebCrypto yet.
    return null;
  }
}

/**
 * Create CryptoKey pair for a device from raw Ed25519 seed bytes.
 * Returns an Ed25519 signing key, an optional X25519 ECDH CryptoKey (null if WebCrypto
 * X25519 is not supported by the browser), and the raw X25519 scalar for noble fallback.
 */
export async function createDeviceCryptoKeys(
  secretKey: Uint8Array,
  publicKey: Uint8Array,
  encryptionSecretKey?: Uint8Array
): Promise<{ signingKey: SigningKey; ecdhKey: CryptoKey | null; ecdhScalar: Uint8Array }> {
  const subtle = getSubtleCrypto();
  const jwk = createEd25519PrivateJwk(secretKey, publicKey);
  let signingKey: SigningKey;
  if (!subtle) {
    signingKey = createFallbackSigningKey(secretKey, publicKey);
  } else {
    try {
      signingKey = await subtle.importKey('jwk', jwk, { name: 'Ed25519' }, false, ['sign']);
    } catch {
      signingKey = createFallbackSigningKey(secretKey, publicKey);
    }
  }

  let scalar: Uint8Array;
  if (encryptionSecretKey) {
    scalar = encryptionSecretKey.length > 32 ? encryptionSecretKey.slice(0, 32) : encryptionSecretKey.slice();
  } else {
    // Legacy fallback: derive X25519 scalar from Ed25519 seed (RFC 8032 clamping).
    // New devices should pass an independent X25519 secret key instead.
    const seed = secretKey.length === 64 ? secretKey.slice(0, 32) : secretKey;
    const hash = sha512(seed);
    scalar = hash.slice(0, 32);
    scalar[0] &= 248;
    scalar[31] &= 127;
    scalar[31] |= 64;
  }

  // X25519 WebCrypto was added in Chrome 133, Firefox 130, Safari 15.4.
  // Older Chrome (113-132) supports Ed25519 but not X25519. Fall back gracefully.
  let ecdhKey: CryptoKey | null = null;
  if (subtle) {
    try {
      ecdhKey = await subtle.importKey('raw', toArrayBuffer(scalar), { name: 'X25519' }, false, ['deriveBits']);
    } catch {
      // noble x25519 (via ecdhScalar) will be used as fallback for ECDH operations.
    }
  }

  return { signingKey, ecdhKey, ecdhScalar: scalar };
}

export async function signWithSigningKey(
  message: Uint8Array,
  signingKey: SigningKey
): Promise<Uint8Array> {
  if (isFallbackEd25519SigningKey(signingKey)) {
    return signEd25519(message, signingKey.privateKey, signingKey.privateKeyJwk);
  }
  return new Uint8Array(await crypto.subtle.sign('Ed25519', signingKey, toArrayBuffer(message)));
}

export async function signEd25519(
  message: Uint8Array,
  privateKey: Uint8Array,
  privateKeyJwk?: JsonWebKey
): Promise<Uint8Array> {
  if (privateKeyJwk) {
    const nativeSignature = await signWithNativeEd25519(message, privateKeyJwk);
    if (nativeSignature) return nativeSignature;
  }

  // Fallback for old key material or unsupported WebCrypto.
  const privateKey32 = normalizeEd25519PrivateKey32(privateKey);
  return ed25519.sign(message, privateKey32);
}

export async function verifyEd25519(
  message: Uint8Array,
  signature: Uint8Array,
  publicKey: Uint8Array
): Promise<boolean> {
  const nativeResult = await verifyWithNativeEd25519(message, signature, publicKey);
  if (nativeResult === true) return true;
  return ed25519.verify(signature, message, publicKey);
}

export async function hasEd25519SigningSupport(): Promise<boolean> {
  try {
    const secretKey = crypto.getRandomValues(new Uint8Array(32));
    const publicKey = ed25519.getPublicKey(secretKey);
    const message = new TextEncoder().encode('circlus-ed25519-support-check');
    const { signingKey } = await createDeviceCryptoKeys(secretKey, publicKey);
    const signature = await signWithSigningKey(message, signingKey);
    return verifyEd25519(message, signature, publicKey);
  } catch {
    return false;
  }
}
